import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { type ChartTypeRegistry } from "chart.js";
import { type IUser } from "src/components/shared/UserList/UserList.types";
import { type ICheckList, type ISection } from "src/interfaces";
import {
  type IReportData,
  type IReportDetails,
  type IYachtData,
} from "src/pages/Reports/Report/Report.types";

export type TReportMode =
  | "all"
  | "entries"
  | "checkLists"
  | "attachments"
  | "entriesAndCheckLists"
  | "entriesAndAttachments";

export interface IChart {
  // Unique across a column's chart configs — a column can have more than one
  // chart (e.g. a count pie and a sum pie side by side).
  id: string;
  name: string;
  sectionIndex: number;
  // Backend hint for whether this column's values are numeric, from
  // ICol.dataType. Undefined when the backend hasn't set it — falls back to
  // sniffing actual cell values with isNumericValue.
  dataType?: "categorical" | "numeric" | "date";
  type: keyof ChartTypeRegistry;
  vals: any[];
  // One entry per section row, blanks included, so charts from the same
  // section stay index-aligned and can be paired for summing.
  rawVals: Array<string | number | undefined>;
  // Index-aligned with rawVals: the entry row each value came from.
  rowRefs: IChartRowRef[];
  il?: string;
  unit?: string;
  dates: string[];
  trendline: boolean;
  valueSource?: TChartValueSource;
  sumVals?: Array<string | number | undefined>;
  sortBy?: "label" | "value";
  sortDirection?: "asc" | "desc";
  // Pies only; on unless false.
  groupSmall?: boolean;
  // Not drawn; kept as a value for other charts.
  valueOnly?: boolean;
  title?: string;
  // Empty values = no filter.
  filter?: IChartFilter;
  // The filter column's rawVals, unless it is the chart's own column.
  filterVals?: Array<string | number | undefined>;
}

export interface IChartFilter {
  column: string;
  values: string[];
}

// One row of a column, charted on its own.
export interface IChartCell {
  col: string;
  // Position among the section's rows, sub-rows flattened.
  rowIndex: number;
  // The chart's name: row label and column.
  name: string;
  // Matches the cell's data-trend-cell in the report.
  cellKey: string;
}

export type TSelectedCells = Record<string, Record<number, IChartCell[]>>;

export interface IChartRowRef {
  lbeid: string;
  rowIndex: number;
  date: string;
}

export type TChartValueSource =
  | { kind: "entries" }
  | { kind: "count" }
  | { kind: "sum"; column: string };

export interface ILogbookCharts {
  lbid: string;
  data: IChart[];
}

interface EntriesGeneratorSlice {
  users: IUser[];
  selectingMode: boolean;
  selectedCols: Record<string, Record<number, string[]>>;
  selectedCells: TSelectedCells;
  chartsData: ILogbookCharts[];
  logbooks: Array<{ label: string; value: string }>;
  selectedLogbook: string;
  sections: Record<string, ISection[]>;
  yachtdata: IYachtData | null;
  reportDetails: IReportDetails | null;
  checkLists: ICheckList[];
  reportMode: TReportMode;
  emptyEntries: boolean;
  chartsIncludeUnverified: boolean;
}

const initialState: EntriesGeneratorSlice = {
  users: [],
  selectingMode: false,
  selectedCols: {},
  selectedCells: {},
  chartsData: [],
  logbooks: [],
  selectedLogbook: "",
  sections: {},
  yachtdata: null,
  reportDetails: null,
  checkLists: [],
  reportMode: "entries",
  emptyEntries: true,
  chartsIncludeUnverified: true,
};

export const entriesGeneratorSlice = createSlice({
  name: "entriesGeneratorSlice",
  initialState,
  reducers: {
    setReportData: (state, action: PayloadAction<IReportData>) => {
      state.users = action.payload?.users;
      state.reportDetails = action.payload?.report;
      state.selectingMode = false;
      state.selectedCols = {};
      state.selectedCells = {};

      if (action.payload?.yachtdata) state.yachtdata = action.payload.yachtdata;

      if (
        action.payload?.report?.type === 0 ||
        action.payload?.report?.type === 2
      ) {
        const logbooks: Array<{ label: string; value: string }> = [];
        const sections: Record<string, ISection[]> = {};

        if (typeof action.payload?.result === "string") return;

        action.payload.result.forEach(entry => {
          if (!logbooks.find(log => log.value === entry.lbid)) {
            logbooks.push({ label: entry.lbname, value: entry.lbid });
            sections[entry.lbid] = entry.lbentry.sections;
          }
        });

        logbooks.sort(({ label: a }, { label: b }) =>
          a.localeCompare(b, undefined, { sensitivity: "base" }),
        );

        state.logbooks = logbooks;
        state.sections = sections;

        state.selectedLogbook = logbooks[0]?.value;
      }
    },
    setCheckLists: (state, action) => {
      state.checkLists = action.payload;
    },
    setUsers: (state, action: PayloadAction<IUser[]>) => {
      state.users = action.payload;
    },
    updateUserContactNumber: (
      state,
      action: PayloadAction<{ userid: string; contactNumber: string }>,
    ) => {
      const { userid, contactNumber } = action.payload;
      const userIndex = state.users.findIndex(user => user.userid === userid);
      if (userIndex !== -1) {
        const user = state.users[userIndex];
        // Update the field that currently contains the contact number, or mobile if both are empty
        if (user.mobile) {
          // If user has mobile, update mobile
          state.users[userIndex] = {
            ...state.users[userIndex],
            mobile: contactNumber,
          };
        } else if (user.phoneno) {
          // If user has phoneno but no mobile, update phoneno
          state.users[userIndex] = {
            ...state.users[userIndex],
            phoneno: contactNumber,
          };
        } else {
          // If user has neither, default to mobile
          state.users[userIndex] = {
            ...state.users[userIndex],
            mobile: contactNumber,
          };
        }
      }
    },
    setReportMode: (state, action: PayloadAction<TReportMode>) => {
      state.reportMode = action.payload;
    },
    setSelectedLogbook: (state, action: PayloadAction<string>) => {
      state.selectedLogbook = action.payload;
    },
    toggleSelectingMode: state => {
      state.selectingMode = !state.selectingMode;
    },
    setYachtdata: (state, action) => {
      state.yachtdata = action.payload;
    },
    handleSpec: (
      state,
      action: PayloadAction<{ section: number; col: string; logbook?: string }>,
    ) => {
      const targetLogbook = action.payload.logbook ?? state.selectedLogbook;
      const selectedLogCols = state.selectedCols[targetLogbook] || {};
      const { section, col } = action.payload;
      if (!selectedLogCols[section]) {
        selectedLogCols[section] = [col];
      } else {
        if (selectedLogCols[section].includes(col)) {
          selectedLogCols[section] = selectedLogCols[section].filter(
            c => c !== col,
          );
          state.chartsData = state.chartsData
            .map(log =>
              log.lbid === targetLogbook
                ? {
                    ...log,
                    data: log.data.filter(
                      chart =>
                        chart.name !== col || chart.sectionIndex !== section,
                    ),
                  }
                : log,
            )
            .filter(log => log.data.length > 0);
        } else {
          selectedLogCols[section].push(col);
        }
      }
      state.selectedCols = {
        ...state.selectedCols,
        [targetLogbook]: selectedLogCols,
      };
    },
    toggleChartCell: (
      state,
      action: PayloadAction<{
        logbook: string;
        section: number;
        cell: IChartCell;
      }>,
    ) => {
      const { logbook, section, cell } = action.payload;
      const cells = state.selectedCells[logbook]?.[section] ?? [];
      const selected = cells.some(c => c.cellKey === cell.cellKey);
      if (selected)
        state.chartsData = state.chartsData
          .map(log =>
            log.lbid === logbook
              ? {
                  ...log,
                  data: log.data.filter(
                    chart =>
                      chart.name !== cell.name ||
                      chart.sectionIndex !== section,
                  ),
                }
              : log,
          )
          .filter(log => log.data.length > 0);
      state.selectedCells = {
        ...state.selectedCells,
        [logbook]: {
          ...state.selectedCells[logbook],
          [section]: selected
            ? cells.filter(c => c.cellKey !== cell.cellKey)
            : [...cells, cell],
        },
      };
    },
    setLogbookCharts: (
      state,
      action: PayloadAction<{ lbid: string; data: IChart[] }>,
    ) => {
      state.chartsData = state.chartsData.map(log =>
        log.lbid === action.payload.lbid
          ? { ...log, data: action.payload.data }
          : log,
      );
    },
    setChartsData: (
      state,
      action: PayloadAction<Array<{ lbid: string; data: IChart[] }>>,
    ) => {
      state.chartsData = action.payload;
      state.selectingMode = false;
    },
    setChartsIncludeUnverified: (state, action: PayloadAction<boolean>) => {
      state.chartsIncludeUnverified = action.payload;
    },
    restoreSavedCharts: (
      state,
      action: PayloadAction<{
        selectedCols: Record<string, Record<number, string[]>>;
        selectedCells?: TSelectedCells;
        chartsData: ILogbookCharts[];
        includeUnverified: boolean;
      }>,
    ) => {
      state.selectedCols = action.payload.selectedCols;
      state.selectedCells = action.payload.selectedCells ?? {};
      state.chartsData = action.payload.chartsData;
      state.chartsIncludeUnverified = action.payload.includeUnverified;
    },
    resetCharts: state => {
      state.chartsData = [];
      state.selectedCols = {};
      state.selectedCells = {};
    },

    setIsEmpty: (state, action: PayloadAction<boolean>) => {
      state.emptyEntries = action.payload;
    },
    resetReportData: () => initialState,
  },
});

export const {
  setReportData,
  setReportMode,
  setSelectedLogbook,
  setCheckLists,
  setUsers,
  updateUserContactNumber,
  toggleSelectingMode,
  handleSpec,
  setChartsData,
  toggleChartCell,
  setLogbookCharts,
  setChartsIncludeUnverified,
  restoreSavedCharts,
  resetCharts,
  resetReportData,
  setYachtdata,
  setIsEmpty,
} = entriesGeneratorSlice.actions;

export default entriesGeneratorSlice.reducer;
